import { useTranslate } from '@tolgee/react';
import 'firebase/auth';
import { SigninWithPasswordForm } from 'lincd-auth/components/SigninWithPasswordForm';
import { useAuth } from 'lincd-auth/hooks/useAuth';
import { AuthenticationResponse } from 'lincd-auth/types/auth';
import { SigninWithAppleButton } from 'lincd-capacitor/components/SigninWithAppleButton';
import { SigninWithGoogleButton } from 'lincd-capacitor/components/SigninWithGoogleButton';
import { SigninWithWhatsappButton } from 'lincd-capacitor/components/SignInWithWhatsapp';
import { Event } from 'lincd-irlcg/shapes/Event';
import { asset } from 'lincd/utils/LinkedFileStorage';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Divider from '../components/atoms/Divider.js';
import { LogoSpinning } from '../components/atoms/LogoSpinning.js';
import ConferenceCard from '../components/molecules/ConferenceCard.js';
import { SwitchLanguage } from '../components/molecules/SwitchLanguage.js';
import { DefaultLayout } from '../layout/DefaultLayout.js';
import { ROUTES } from '../routes.js';
import style from './Signin.module.css';

export default function Signin() {
  let { t } = useTranslate();
  //with t(key,defaultValue) the key will show up on in the dev context app (hover text and hold Option) and can be added and translated in tolgee.
  let prefix = 'signin';
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const isNavigatingToRegister = useRef(false);
  const [oauthError, setOauthError] = useState<string>('');

  const onLoggedIn = () => {
    navigate(ROUTES.index.path);
  };

  // handle oauth callback
  const onOAuthCallback = async (response: AuthenticationResponse | any) => {
    const err =
      typeof response?.error === 'string' ? response.error.trim() : undefined;

    // Some auth paths may return an empty `error` string; fall back to a generic message.
    if (err !== undefined) {
      const msg =
        err.length > 0
          ? err
          : t(
              prefix + '.oauthErrorGeneric',
              'Sign-in failed. Please try again.'
            );
      setOauthError(msg);
      return;
    }
    setOauthError('');
    onLoggedIn();
  };

  // clear oauth error after 10 seconds
  useEffect(() => {
    if (!oauthError) return;
    const timeoutId = setTimeout(() => {
      setOauthError('');
    }, 10 * 1000); // 10 seconds
    return () => clearTimeout(timeoutId);
  }, [oauthError]);

  useEffect(() => {
    if (auth.userAccount) {
      // don't redirect if user is navigating to register (e.g., from event card click)
      // check both current location and if we're in the process of navigating to register
      const currentPath = location.pathname;
      const hasUriParam = new URLSearchParams(location.search).has('uri');

      if (
        currentPath === ROUTES.register.path ||
        isNavigatingToRegister.current ||
        hasUriParam
      ) {
        setLoading(false);
        return;
      }

      navigate(ROUTES.index.path);
    } else {
      setLoading(false);
    }
  }, [auth.userAccount, location.pathname, location.search]);

  const getAllEvents = async () => {
    try {
      const events = await Event.getActive();
      setEvents(events || []);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAllEvents();
  }, []);

  return (
    <DefaultLayout
      backgroundColor="dark"
      safeareaBackground={'var(--ld-app-body-background-color)'}
    >
      <div className={style.SigninContainer} aria-labelledby="signin-container">
        {loading ? (
          <LogoSpinning centered={true} aria-label="Loading spinner" />
        ) : (
          <>
            <div className={style.Title} aria-labelledby="title">
              <img
                src={asset('/images/peace-game-logo-spinning-2.gif')}
                style={{ width: '150px', height: 'auto' }}
                alt="Peace Game logo"
                aria-label={'Peace Game logo'}
              />
              <h2>{t('app-name', 'Peace Game')}</h2>
            </div>
            <div className={style.Form} aria-labelledby="signin-form">
              <div className={style.Signin}>
                <h2>{t(prefix + '.playerSignIn', 'Player Sign In')}</h2>
                <SigninWithPasswordForm
                  // className={style.SocialSignin}
                  onLoadingChange={(loading) => setLoading(loading)}
                  startWithInputField={true}
                  showEmailIcon={true}
                  onLoggedIn={onLoggedIn}
                  aria-label={t(
                    'password-signin-form',
                    'Sign in with password form'
                  )}
                />
              </div>
              <div className={style.Divider} aria-hidden="true">
                <Divider />
              </div>
              <div
                className={style.SocialSignin}
                aria-label={t(
                  'whatsapp-signin-button',
                  'Sign in with WhatsApp button'
                )}
              >
                <SigninWithWhatsappButton
                  onSuccess={onLoggedIn}
                  aria-label="Sign in with WhatsApp button"
                />
              </div>
              <div
                className={style.SocialSignin}
                aria-labelledby="signin-with-google"
              >
                <SigninWithGoogleButton
                  onCallback={onOAuthCallback}
                  onLoadingChange={(loading) => {
                    setLoading(loading);
                    if (loading) {
                      setOauthError('');
                    }
                  }}
                  aria-label={t(
                    'google-signin-button',
                    'Sign in with Google button'
                  )}
                />
              </div>

              <div
                className={style.SocialSignin}
                aria-labelledby="signin-with-apple"
              >
                <SigninWithAppleButton
                  onCallback={onOAuthCallback}
                  onLoadingChange={(loading) => {
                    setLoading(loading);
                    if (loading) {
                      setOauthError('');
                    }
                  }}
                  aria-label={t(
                    'apple-signin-button',
                    'Sign in with Apple button'
                  )}
                />
              </div>

              {oauthError ? (
                <div className={style.oauthError} role="alert">
                  {oauthError}
                </div>
              ) : null}

              {!loading && events.length > 0 && (
                <>
                  <h3>Event Registration</h3>
                  {events.map((event) => (
                    <ConferenceCard
                      key={event.id}
                      id={event.id}
                      name={event.name}
                      description={event.description}
                      image={event.image}
                      thumbnailWidth={150}
                      onClick={async () => {
                        isNavigatingToRegister.current = true;
                        auth
                          .signinTemporary()
                          .then((response) => {
                            navigate(`${ROUTES.register.path}?uri=${event.id}`);
                          })
                          .catch((err) => {
                            console.error('Signin temporary failed:', err);
                            isNavigatingToRegister.current = false;
                          });
                      }}
                    />
                  ))}
                </>
              )}

              <div className={style.Divider} aria-hidden="true">
                <Divider />
              </div>
            </div>
            <div className={style.Terms} aria-labelledby="terms-and-conditions">
              <p>
                {t(
                  prefix + '.terms.1',
                  'By selecting "Create Player Account" or "Player Sign In" you agree to our'
                )}{' '}
                <Link to={ROUTES.terms.path} aria-label={t('terms', 'Terms')}>
                  {t(prefix + '.terms', 'Terms')}
                </Link>
                .{' '}
                {t(
                  prefix + '.privacy.1',
                  'Learn how the platform uses your data in our'
                )}{' '}
                <Link
                  to={ROUTES.privacy_policy.path}
                  aria-label={t('privacy-policy', 'Privacy Policy')}
                >
                  {t(prefix + '.privacyPolicy', 'Privacy Policy')}
                </Link>
                .
              </p>
              <Link
                to={ROUTES.contact.path}
                className={style.contactSupport}
                aria-label={t('contact-support', 'Contact support')}
              >
                <img width={24} src={asset('/images/icon-support-white.svg')} />
                <p>{t(prefix + '.contactSupport', 'Contact support')}</p>
              </Link>
              <SwitchLanguage className={style.switchLanguage} />
            </div>
          </>
        )}
      </div>
    </DefaultLayout>
  );
}
